/* Phase 2F — Social Strategy panel.
 *
 * Renders the persisted launch-ready social strategy report produced
 * by `backend/social_strategy.py`. The panel is honesty-first:
 *   - top banner = locked honesty disclaimer + assumption-mode pill
 *     when applicable
 *   - every section carries a source-label chip from the locked
 *     Phase 2E-lite + Phase 2F vocabulary
 *   - profile registry is rendered from deterministic data, never LLM
 *
 * Backend contract:
 *   GET  /api/projects/{id}/social-strategy
 *   POST /api/projects/{id}/social-strategy/generate
 *   GET  /api/exports/{id}/social-strategy.pdf
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    BookOpen, RefreshCw, Sparkles, AlertCircle, Download,
    Quote, Compass, Layers, Megaphone, Hash, Calendar, Users, Send,
    ListChecks,
} from "lucide-react";
import { downloadExport } from "@/lib/exports";

const SOURCE_TONE = {
    user_provided:             "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
    website_verified:          "border-sky-500/30 text-sky-300 bg-sky-500/5",
    reused_foundation:         "border-violet-500/30 text-violet-300 bg-violet-500/5",
    ai_inference:              "border-amber-500/30 text-amber-300 bg-amber-500/5",
    profile_discovered:        "border-zinc-700 text-zinc-300 bg-zinc-900/40",
    profile_confirmed:         "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
    distribution_ai_inference: "border-amber-500/30 text-amber-300 bg-amber-500/5",
    assumption_mode:           "border-rose-500/30 text-rose-300 bg-rose-500/5",
};

function SourceChip({ label, className = "" }) {
    const tone = SOURCE_TONE[label] || SOURCE_TONE.ai_inference;
    return (
        <span
            className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${tone} ${className}`}
            data-testid={`source-chip-${label || "ai_inference"}`}
        >
            {(label || "ai_inference").replace(/_/g, " ")}
        </span>
    );
}

function SectionHeader({ icon: Icon, title, label, testId }) {
    return (
        <div className="flex items-center gap-3 mb-3 flex-wrap" data-testid={testId}>
            <Icon className="w-4 h-4 text-zinc-500" />
            <h3 className="font-display text-lg">{title}</h3>
            {label && <SourceChip label={label} />}
        </div>
    );
}

export default function SocialStrategyPanel({ projectId }) {
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [honesty, setHonesty] = useState("");

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const { data } = await api.get(
                `/projects/${projectId}/social-strategy`
            );
            setDoc(data.report || null);
            setHonesty(data.honesty_notice || "");
        } catch (e) {
            toast.error(e.userMessage || "Could not load social strategy");
        } finally { setLoading(false); }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    const generate = async () => {
        if (!projectId) return;
        setGenerating(true);
        try {
            const { data } = await api.post(
                `/projects/${projectId}/social-strategy/generate`
            );
            setDoc(data.report || null);
            setHonesty(data.honesty_notice || "");
            toast.success(
                data.report?.assumption_mode
                    ? "Generated in assumption mode — add Brand DNA + Research for richer output."
                    : "Social strategy generated."
            );
        } catch (e) {
            toast.error(e.userMessage || "Could not generate strategy");
        } finally { setGenerating(false); }
    };

    const downloadPdf = async () => {
        // Phase Final-QA — use the shared exports helper so the
        // localStorage key matches the rest of the app (aivos_token,
        // not "token") and we get proper progress/error UX.
        // NOTE: API base already includes `/api`, so the path here
        // MUST start at `/exports/...` (not `/api/exports/...`) to
        // avoid producing a doubled `/api/api/...` 404.
        await downloadExport(
            `/exports/${projectId}/social-strategy.pdf`,
            `social-strategy-${projectId}.pdf`,
            (err) => toast.error(err?.message || "PDF download failed"),
        );
    };

    const report = doc?.report || null;
    const generatedAt = doc?.generated_at;

    return (
        <div className="card-base p-5" data-testid="social-strategy-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-3 min-w-0">
                    <BookOpen className="w-4 h-4 text-zinc-500" />
                    <h3 className="font-display text-lg">Social strategy report</h3>
                    {generatedAt && (
                        <span className="text-xs text-zinc-500" data-testid="ss-generated-at">
                            {new Date(generatedAt).toLocaleString()}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {doc && (
                        <button
                            type="button"
                            onClick={downloadPdf}
                            data-testid="ss-download-pdf-btn"
                            className="btn-secondary text-xs"
                        >
                            <Download className="w-3.5 h-3.5" />
                            PDF
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={generate}
                        disabled={generating}
                        data-testid="ss-generate-btn"
                        className="btn-secondary text-xs disabled:opacity-50"
                    >
                        {generating
                            ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            : <Sparkles className="w-3.5 h-3.5" />}
                        {doc ? "Regenerate" : "Generate"}
                    </button>
                </div>
            </div>

            <HonestyBanner
                honesty={honesty}
                assumptionMode={doc?.assumption_mode}
            />

            {loading && (
                <div className="text-xs text-zinc-500" data-testid="ss-loading">
                    Loading…
                </div>
            )}

            {!loading && !doc && (
                <div
                    className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-sm p-6 text-center"
                    data-testid="ss-empty"
                >
                    No social strategy report yet.
                    <div className="text-xs mt-2 text-zinc-600">
                        Click <span className="text-zinc-300">Generate</span> above to
                        fuse Brand DNA, Research, Concepts, Profile Registry and
                        Distribution into a launch-ready report.
                    </div>
                </div>
            )}

            {!loading && report && (
                <Report report={report} />
            )}
        </div>
    );
}

function HonestyBanner({ honesty, assumptionMode }) {
    return (
        <div className="space-y-2 mb-5" data-testid="ss-honesty-banner">
            <div className="text-xs text-zinc-500 leading-relaxed border-l-2 border-violet-500/30 pl-3 bg-violet-500/[0.03] py-2 rounded-r-sm">
                <span className="text-violet-300 font-medium">Honesty notice.</span>{" "}
                {honesty || (
                    "Social platform content was not deeply analyzed. " +
                    "Recommendations are based on brand/project data, website " +
                    "research where available, confirmed/profile-discovered " +
                    "social links, and strategic inference."
                )}
            </div>
            {assumptionMode && (
                <div
                    className="text-xs text-rose-300 leading-relaxed border-l-2 border-rose-500/40 pl-3 bg-rose-500/[0.04] py-2 rounded-r-sm"
                    data-testid="ss-assumption-banner"
                >
                    <span className="font-medium">No platform content evidence.</span>{" "}
                    No user-uploaded screenshots, exports, or official social-API
                    data was available. Recommendations come from brand/project
                    foundation, website research, social profile registry,
                    distribution rule engine, influencer suggestions, and
                    strategic inference — not from analyzed platform content.
                </div>
            )}
        </div>
    );
}

function Report({ report }) {
    const src = report.source_summary || {};
    return (
        <div className="space-y-7" data-testid="ss-report">
            {/* Executive summary */}
            <section>
                <SectionHeader
                    icon={Quote}
                    title="Executive summary"
                    label={src.executive_summary || report.executive_summary?.source_label}
                    testId="ss-executive-summary"
                />
                <p className="text-sm text-zinc-200 leading-relaxed">
                    {textOf(report.executive_summary)}
                </p>
            </section>

            {/* Social presence */}
            <section>
                <SectionHeader
                    icon={Compass}
                    title="Social presence summary"
                    label={src.social_presence_summary || report.social_presence_summary?.source_label}
                    testId="ss-social-presence"
                />
                <p className="text-sm text-zinc-200 leading-relaxed">
                    {textOf(report.social_presence_summary)}
                </p>
            </section>

            {/* Profile registry */}
            <ProfileRegistrySummary registry={report.profile_registry_summary} />

            {/* Platform recommendations */}
            {(report.platform_recommendations || []).length > 0 && (
                <section data-testid="ss-platform-recommendations">
                    <SectionHeader
                        icon={Layers}
                        title="Platform recommendations"
                        label={src.platform_recommendations}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        {report.platform_recommendations.map((p, idx) => (
                            <div
                                key={`${p.platform}-${idx}`}
                                className="border border-zinc-800 bg-zinc-950/50 rounded-sm px-3 py-2.5"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm text-zinc-100 font-medium">
                                        {p.platform}
                                    </span>
                                    <SourceChip label={p.source_label} />
                                </div>
                                <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mt-1">
                                    route: {p.route}
                                </div>
                                <p className="text-xs text-zinc-300 mt-1.5 leading-relaxed">
                                    {p.why}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Content pillars */}
            {(report.content_pillars || []).length > 0 && (
                <section data-testid="ss-content-pillars">
                    <SectionHeader
                        icon={Layers}
                        title="Content pillars"
                        label={src.content_pillars}
                    />
                    <div className="space-y-3">
                        {report.content_pillars.map((c, idx) => (
                            <div
                                key={`${c.pillar}-${idx}`}
                                className="border-l-2 border-violet-500/30 pl-3"
                            >
                                <div className="text-sm text-zinc-100 font-medium">
                                    {c.pillar}
                                </div>
                                <p className="text-xs text-zinc-400 leading-relaxed mt-0.5">
                                    {c.why_it_matters}
                                </p>
                                {Array.isArray(c.sample_themes) && c.sample_themes.length > 0 && (
                                    <ul className="text-xs text-zinc-400 ml-4 list-disc mt-1 space-y-0.5">
                                        {c.sample_themes.slice(0, 5).map((t, i) => (
                                            <li key={i}>{t}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Reel series */}
            {(report.reel_series_ideas || []).length > 0 && (
                <ListSection
                    icon={Megaphone}
                    title="Reel series ideas"
                    label={src.reel_series_ideas}
                    items={report.reel_series_ideas}
                    testId="ss-reel-series"
                    render={(r) => (
                        <>
                            <div className="text-sm text-zinc-100 font-medium">
                                {r.series_name}
                            </div>
                            <p className="text-xs text-zinc-300 italic">"{r.hook}"</p>
                            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mt-1">
                                {r.episode_count} episodes · {r.format}
                            </div>
                        </>
                    )}
                />
            )}

            {/* Talk show / interview */}
            {(report.talk_show_or_interview_formats || []).length > 0 && (
                <ListSection
                    icon={Megaphone}
                    title="Talk show / interview formats"
                    label={src.talk_show_or_interview_formats}
                    items={report.talk_show_or_interview_formats}
                    testId="ss-talk-formats"
                    render={(t) => (
                        <>
                            <div className="text-sm text-zinc-100 font-medium">
                                {t.format_name}
                            </div>
                            <p className="text-xs text-zinc-300 leading-relaxed">{t.premise}</p>
                            {Array.isArray(t.guests) && t.guests.length > 0 && (
                                <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mt-1">
                                    guests: {t.guests.slice(0, 4).join(" · ")}
                                </div>
                            )}
                        </>
                    )}
                />
            )}

            {/* Hook bank */}
            {(report.hook_bank || []).length > 0 && (
                <section data-testid="ss-hook-bank">
                    <SectionHeader icon={Quote} title="Hook bank" label={src.hook_bank} />
                    <div className="space-y-1.5">
                        {report.hook_bank.slice(0, 20).map((h, idx) => (
                            <div
                                key={idx}
                                className="text-sm text-zinc-200 border-l-2 border-amber-500/20 pl-3"
                            >
                                "{typeof h === "string" ? h : h.hook}"
                                {h.hook_type && (
                                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 ml-2">
                                        · {h.hook_type}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Caption + CTA */}
            {(report.caption_cta_bank || []).length > 0 && (
                <section data-testid="ss-caption-cta">
                    <SectionHeader
                        icon={Send}
                        title="Caption + CTA bank"
                        label={src.caption_cta_bank}
                    />
                    <div className="space-y-2.5">
                        {report.caption_cta_bank.slice(0, 12).map((cc, idx) => (
                            <div
                                key={idx}
                                className="border border-zinc-800 bg-zinc-950/50 rounded-sm px-3 py-2"
                            >
                                <p className="text-sm text-zinc-200 leading-relaxed">
                                    {cc.caption}
                                </p>
                                <div className="text-xs text-zinc-400 mt-1">
                                    <span className="text-emerald-300">CTA:</span> {cc.cta}
                                    {cc.tone && (
                                        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 ml-3">
                                            · {cc.tone}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Hashtag direction */}
            {report.hashtag_direction && (
                <section data-testid="ss-hashtag-direction">
                    <SectionHeader
                        icon={Hash}
                        title="Hashtag direction"
                        label={src.hashtag_direction || report.hashtag_direction?.source_label}
                    />
                    <HashtagBlock direction={report.hashtag_direction} />
                </section>
            )}

            {/* 30-day plan */}
            {(report["30_day_content_plan"] || []).length > 0 && (
                <section data-testid="ss-30-day-plan">
                    <SectionHeader
                        icon={Calendar}
                        title="30-day content plan"
                        label={src["30_day_content_plan"]}
                    />
                    <PlanTable plan={report["30_day_content_plan"]} />
                </section>
            )}

            {/* Influencers */}
            {(report.influencer_collaboration_ideas || []).length > 0 && (
                <ListSection
                    icon={Users}
                    title="Influencer collaboration ideas"
                    label={src.influencer_collaboration_ideas}
                    items={report.influencer_collaboration_ideas}
                    testId="ss-influencer-ideas"
                    render={(i) => (
                        <>
                            <div className="text-sm text-zinc-100 font-medium">
                                {i.tier} · {i.archetype}
                            </div>
                            <p className="text-xs text-zinc-300 leading-relaxed">{i.why}</p>
                            {i.ask && (
                                <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mt-1">
                                    ask: {i.ask}
                                </div>
                            )}
                        </>
                    )}
                />
            )}

            {/* Distribution plan */}
            {(report.distribution_plan || []).length > 0 && (
                <ListSection
                    icon={Compass}
                    title="Distribution plan"
                    label={src.distribution_plan || "distribution_ai_inference"}
                    items={report.distribution_plan}
                    testId="ss-distribution-plan"
                    render={(d) => (
                        <>
                            <div className="text-sm text-zinc-100 font-medium">
                                {d.channel}
                            </div>
                            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">
                                route: {d.route}
                            </div>
                            <p className="text-xs text-zinc-300 leading-relaxed mt-1">{d.why}</p>
                            {d.first_30_day_action && (
                                <div className="text-xs text-emerald-300 border-l-2 border-emerald-500/30 pl-2 mt-1.5">
                                    <span className="text-[10px] font-mono uppercase tracking-wider">
                                        First 30 days:
                                    </span>{" "}
                                    {d.first_30_day_action}
                                </div>
                            )}
                        </>
                    )}
                />
            )}

            {/* Assumptions & limitations */}
            <AssumptionsBlock block={report.assumptions_and_limitations} />

            {/* Source summary */}
            <SourceSummaryBlock summary={report.source_summary} />
        </div>
    );
}

function textOf(v) {
    if (!v) return "—";
    if (typeof v === "string") return v;
    if (typeof v.text === "string") return v.text;
    return "—";
}

function ProfileRegistrySummary({ registry }) {
    const all = useMemo(() => {
        if (!registry) return [];
        const items = [];
        (registry.confirmed || []).forEach((r) => items.push({ ...r, _group: "confirmed" }));
        (registry.discovered_from_website || []).forEach((r) =>
            items.push({ ...r, _group: "discovered" })
        );
        (registry.candidate || []).forEach((r) => items.push({ ...r, _group: "candidate" }));
        return items;
    }, [registry]);
    if (!registry) return null;
    return (
        <section data-testid="ss-profile-registry-summary">
            <SectionHeader
                icon={ListChecks}
                title="Profile registry summary"
                label={registry.source_label}
            />
            <div className="flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-wider mb-3">
                <span className="text-zinc-300">total {registry.total ?? 0}</span>
                <span className="text-emerald-300">
                    confirmed {registry.confirmed_count ?? 0}
                </span>
                <span className="text-sky-300">
                    discovered {registry.discovered_count ?? 0}
                </span>
                <span className="text-zinc-400">
                    candidate {registry.candidate_count ?? 0}
                </span>
            </div>
            {all.length === 0 ? (
                <div className="text-xs text-zinc-500 italic">
                    No social profiles on record yet.
                </div>
            ) : (
                <div className="space-y-1.5">
                    {all.map((r, idx) => (
                        <div
                            key={idx}
                            className="flex items-center gap-2 flex-wrap text-xs"
                        >
                            <span className="text-zinc-100 font-medium">{r.platform}</span>
                            <span className="text-zinc-400 truncate max-w-[260px]">
                                {r.handle || r.profile_url}
                            </span>
                            <SourceChip label={r.source_label} />
                            <span
                                className={
                                    r.verification_status === "confirmed"
                                        ? "text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-emerald-500/30 text-emerald-300 bg-emerald-500/5"
                                        : "text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-300 bg-zinc-900/40"
                                }
                            >
                                {(r.verification_status || "needs_manual_verification").replace(/_/g, " ")}
                            </span>
                        </div>
                    ))}
                </div>
            )}
            <div className="text-[10px] text-zinc-600 mt-3 flex items-center gap-1.5">
                <AlertCircle className="w-3 h-3" />
                {registry.note ||
                    "Profiles are tracked at registry level only. Content was not deeply analyzed."}
            </div>
        </section>
    );
}

function HashtagBlock({ direction }) {
    return (
        <div className="space-y-2 text-xs">
            {["core_set", "geo_set", "niche_set"].map((k) =>
                (direction[k] || []).length > 0 ? (
                    <div key={k}>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                            {k.replace("_set", "")}:
                        </span>{" "}
                        <span className="text-zinc-200">
                            {(direction[k] || []).slice(0, 14).join("  ·  ")}
                        </span>
                    </div>
                ) : null
            )}
            {direction.note && (
                <p className="text-[11px] text-zinc-500 mt-1">{direction.note}</p>
            )}
        </div>
    );
}

function PlanTable({ plan }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
                <thead>
                    <tr className="border-b border-zinc-800 text-zinc-500 text-[10px] font-mono uppercase tracking-wider">
                        <th className="text-left py-2 pr-2">Day</th>
                        <th className="text-left py-2 pr-2">Theme</th>
                        <th className="text-left py-2 pr-2">Platform</th>
                        <th className="text-left py-2 pr-2">Format</th>
                        <th className="text-left py-2">Caption seed</th>
                    </tr>
                </thead>
                <tbody>
                    {plan.slice(0, 30).map((d, idx) => (
                        <tr key={idx} className="border-b border-zinc-900">
                            <td className="py-1.5 pr-2 text-zinc-300">{d.day}</td>
                            <td className="py-1.5 pr-2 text-zinc-200">{d.theme}</td>
                            <td className="py-1.5 pr-2 text-zinc-400">{d.platform}</td>
                            <td className="py-1.5 pr-2 text-zinc-400">{d.format}</td>
                            <td className="py-1.5 text-zinc-300">{d.caption_seed}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ListSection({ icon: Icon, title, label, items, render, testId }) {
    return (
        <section data-testid={testId}>
            <SectionHeader icon={Icon} title={title} label={label} />
            <div className="space-y-2.5">
                {items.slice(0, 8).map((it, idx) => (
                    <div
                        key={idx}
                        className="border border-zinc-800 bg-zinc-950/50 rounded-sm px-3 py-2.5"
                    >
                        {render(it)}
                    </div>
                ))}
            </div>
        </section>
    );
}

function AssumptionsBlock({ block }) {
    if (!block) return null;
    return (
        <section data-testid="ss-assumptions">
            <SectionHeader
                icon={AlertCircle}
                title="Assumptions & limitations"
                label={block.source_label || "assumption_mode"}
            />
            {block.honesty_disclaimer && (
                <div className="text-xs text-amber-200 leading-relaxed border-l-2 border-amber-500/30 pl-3 bg-amber-500/[0.04] py-2 rounded-r-sm mb-2">
                    {block.honesty_disclaimer}
                </div>
            )}
            <ul className="text-xs text-zinc-300 space-y-1 ml-4 list-disc">
                {(block.items || []).map((it, idx) => (
                    <li key={idx}>{it}</li>
                ))}
            </ul>
        </section>
    );
}

function SourceSummaryBlock({ summary }) {
    if (!summary || Object.keys(summary).length === 0) return null;
    const entries = Object.entries(summary);
    return (
        <section data-testid="ss-source-summary">
            <SectionHeader
                icon={ListChecks}
                title="Source summary"
                label="reused_foundation"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 text-xs">
                {entries.map(([k, v]) => (
                    <div
                        key={k}
                        className="flex items-center justify-between border-b border-zinc-900 py-1"
                    >
                        <span className="text-zinc-400">{k.replace(/_/g, " ")}</span>
                        <SourceChip label={v} />
                    </div>
                ))}
            </div>
        </section>
    );
}
